import { Dialog, DialogActions, DialogContent, FormControl, Grid, RadioGroup, Stack, Typography } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import { MprcClickable, MprcDialogTitleClose, MprcFormSubHeader, MprcRequestIndicator } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { MprcNumberField } from "components/MprcNumberField";
import { MprcRadio } from "components/MprcRadio";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { DevPlanItemType } from "generated/CoreApi_v1";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////////

interface IData {
    progressPercent: number | null;
    completionDeadline: Date | null;
    ignoreLastApprovedEnrollment: boolean;
}

interface IPros {
    devPlanId: string;
    devPlanGroupId: string;
    devPlanItemId: string;
    title: string;
    type: DevPlanItemType;
    deadline: Date | null;
    progress: number | null;
    isDraft: boolean;
    onClose: (success: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditDevPlanItemDialog: React.FC<IPros> = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { enqueueSnackbar } = useSnackbar();

    const { devPlanId, devPlanGroupId, devPlanItemId, type, title, onClose, isDraft } = props;

    const [completionDeadline, setCompletionDeadline] = useState<Date | null>(props.deadline);
    const [progressPercent, setProgressPercent] = useState<number | null>(props.progress);
    const [ignoreLastApprovedEnrollment, setIgnoreLastApprovedEnrollment] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const loadData = useRef<IData>({
        completionDeadline: props.deadline,
        progressPercent: props.progress,
        ignoreLastApprovedEnrollment: false
    });

    const canSave = useMemo(() => {
        const recordChanged = loadData.current.progressPercent != progressPercent
            || loadData.current.completionDeadline?.getTime() != completionDeadline?.getTime()
            || loadData.current.ignoreLastApprovedEnrollment != ignoreLastApprovedEnrollment;

        return recordChanged && (progressPercent == null || progressPercent <= 100);
    }, [loadData, progressPercent, completionDeadline]);


    ///
    const handleSave = () => {
        setSaving(true);
        api.v1.devPlan.updateDevPlanActivity({
            devPlanId,
            devPlanGroupId,
            devPlanItemId,
            completionDeadline,
            progressPercent,
            ignoreLastApprovedEnrollment
        })
            .then(response => {
                if (response.data === true) {
                    enqueueSnackbar(t("Record.SaveSuccessMessage", { ns: "shared" }), { variant: "success" });
                    onClose(true);
                }
                else {
                    enqueueSnackbar(t("Record.FailSuccessMessage", { ns: "shared" }), { variant: "error" });
                }
            })
            .catch(e => {
                MprcRequestSnackbarErrorReport(e);
            })
            .finally(() => setSaving(false));
    };

    return (
        <>
            <MprcRequestIndicator visible={saving} type={"post"} />
            <Dialog open onClose={() => onClose(false)}>
                <MprcDialogTitleClose
                    title={t("DevPlan.EditItem.Title")}
                    subtitle={title}
                    onClose={() => onClose(false)}
                    shrinkPadding
                />
                <DialogContent>
                    <Grid container rowSpacing={4}>
                        <Grid>
                            <MprcFormSubHeader title={t("DevPlan.EditItem.DeadlineLabel")} />
                            <DateCalendar
                                value={completionDeadline == null ? null : moment(completionDeadline)}
                                disablePast
                                onChange={(newValue) => setCompletionDeadline(newValue?.toDate() ?? null)}
                            />
                        </Grid>
                        {type == DevPlanItemType.Other &&
                            <Grid>
                                <Stack direction="column" spacing={2}>
                                    <MprcFormSubHeader title={t("DevPlan.EditItem.ProgressLabel")} />
                                    <MprcNumberField
                                        value={progressPercent}
                                        sx={{ width: 70 }}
                                        min={0}
                                        max={100}
                                        slotProps={{ input: { endAdornment: "%" } }}
                                        onChange={(value) => setProgressPercent(value)}
                                    />
                                    {(progressPercent ?? 0) < 100 &&
                                        <MprcClickable onClick={() => setProgressPercent(100)}>
                                            <Typography variant="body1" fontWeight="bold" color="highlight.main">
                                                {t("DevPlan.EditItem.MarkCompletedText")}
                                            </Typography>
                                        </MprcClickable>
                                    }
                                </Stack>
                            </Grid>

                        }
                    </Grid>
                    {type == DevPlanItemType.Training &&
                        <>
                            <MprcFormSubHeader
                                titleVariant="overline"
                                title={t("DevPlan.EditItem.IgnoreApprovedEnrollmentLabel")}
                                margin="dense"
                            />
                            <FormControl margin="dense">
                                <RadioGroup
                                    value={ignoreLastApprovedEnrollment}
                                    onChange={(event, value) => setIgnoreLastApprovedEnrollment(value == "true")}
                                >
                                    <MprcRadio
                                        disabled={!isDraft}
                                        label={t("DevPlan.EditItem.IgnoreApprovedEnrollmentOption1")}
                                        value={false}
                                    />
                                    <MprcRadio
                                        disabled={!isDraft}
                                        label={t("DevPlan.EditItem.IgnoreApprovedEnrollmentOption2")}
                                        value={true}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </>
                    }

                </DialogContent>
                <DialogActions>
                    <ButtonCancel variant="contained" onClick={() => onClose(false)} />
                    <ButtonSave
                        disabled={!canSave}
                        variant="contained"
                        onClick={handleSave}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
